import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { User, MessageCircle } from "lucide-react";
import { type MentorData } from "./RecommendedMentorsList";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface ConnectedMentorsListProps {
  isLoading: boolean;
  connectedMentors: MentorData[];
  onViewProfile: (mentorId: string) => void;
  onNavigate: (route: string) => void;
}

const ConnectedMentorsList = ({ 
  isLoading, 
  connectedMentors, 
  onViewProfile, 
  onNavigate 
}: ConnectedMentorsListProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [conversationIds, setConversationIds] = useState<Record<string, string>>({});
  
  useEffect(() => {
    if (!user?.id || connectedMentors.length === 0) return;
    
    const fetchConversationIds = async () => {
      try {
        // Get all conversation IDs for each mentor
        const mentorIds = connectedMentors.map(mentor => mentor.id);
        
        const { data, error } = await supabase
          .from('conversations')
          .select('id, mentor_id')
          .eq('mentee_id', user.id)
          .in('mentor_id', mentorIds);
          
        if (error) {
          console.error("Error fetching conversation IDs:", error);
          return;
        }
        
        if (data && data.length > 0) {
          // Create a map of mentor ID to conversation ID
          const idMap: Record<string, string> = {};
          data.forEach(item => {
            idMap[item.mentor_id] = item.id;
          });
          
          setConversationIds(idMap);
        }
      } catch (error) {
        console.error("Exception in fetchConversationIds:", error);
      }
    };
    
    fetchConversationIds();
  }, [user?.id, connectedMentors]);
  
  const handleMessageClick = async (mentorId: string) => {
    try {
      // If we already have a conversation ID for this mentor, use it
      if (conversationIds[mentorId]) {
        onNavigate(`/messages/${conversationIds[mentorId]}`);
        return;
      }
      
      // Otherwise, create a new conversation
      if (!user?.id) {
        toast({
          title: "Error",
          description: "You must be logged in to send messages.",
          variant: "destructive",
        });
        return;
      }
      
      const { data, error } = await supabase
        .from('conversations')
        .insert({
          mentee_id: user.id,
          mentor_id: mentorId,
        })
        .select('id')
        .single();
        
      if (error) {
        console.error("Error creating conversation:", error);
        toast({
          title: "Error",
          description: "Failed to create conversation. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      // Update the local state with the new conversation ID
      setConversationIds(prev => ({
        ...prev,
        [mentorId]: data.id
      }));
      
      // Navigate to the messages page with the new conversation ID
      onNavigate(`/messages/${data.id}`);
    } catch (error) {
      console.error("Exception in handleMessageClick:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  };
  
  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-800 mb-6">My Mentors</h2>
      
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex flex-col items-center">
                  <Skeleton className="h-20 w-20 rounded-full mb-4" />
                  <Skeleton className="h-5 w-24 mb-2" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : connectedMentors.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {connectedMentors.map((mentor) => (
            <Card key={mentor.id} className="overflow-hidden">
              <CardContent className="p-6 flex flex-col items-center">
                <Avatar className="w-20 h-20 mb-4">
                  <AvatarImage src={mentor.avatar} alt={mentor.name} />
                  <AvatarFallback>{mentor.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="bg-blue-100 text-blue-600 text-xs font-medium px-2.5 py-0.5 rounded-full mb-2">
                  Mentor
                </div>
                <h3 className="text-lg font-semibold text-center">{mentor.name}</h3>
              </CardContent>
              <div className="grid grid-cols-2 border-t">
                <button className="py-3 flex justify-center items-center gap-1.5 hover:bg-gray-50 border-r"
                        onClick={() => onViewProfile(mentor.id)}>
                  <User size={16} className="text-gray-500" />
                </button>
                <button className="py-3 flex justify-center items-center gap-1.5 hover:bg-gray-50"
                        onClick={() => handleMessageClick(mentor.id)}>
                  <MessageCircle size={16} className="text-gray-500" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-4">You don't have any connected mentors yet.</p>
            <Button 
              onClick={() => onNavigate('/search-mentors')}
              variant="action"
            >
              Find Mentors
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ConnectedMentorsList;
